import {
  GetProps,
  Label,
  Switch,
  XStack,
  createStyledContext,
  styled,
} from 'tamagui'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { HeroIcon } from './HeroIcon'
import { useState } from 'react'

interface IHeroSwitchProps extends GetProps<typeof Switch> {
  defaultChecked?: boolean
  side?: 'left' | 'right'
  icon?: IconProp
  disabled?: boolean
  label?: string
}

export const HeroSwitchContext = createStyledContext<{
  disabled: boolean
  defaultChecked: boolean
  icon: boolean | undefined
}>({
  disabled: false,
  defaultChecked: false,
  icon: undefined,
})

export const AppHeroSwitch = styled(Switch, {
  name: 'HeroSwitch',
  context: HeroSwitchContext,
  role: 'switch',
  focusable: true,
  size: 40,
  height: 27,
  width: 40,
  padding: 0,
  backgroundColor: '$control-track-off',
  borderColor: '$control-track-off',
  cursor: 'pointer',
  borderWidth: 3,
  hoverStyle: {
    backgroundColor: '$control-track-off-hover',
    borderColor: '$control-track-off-hover',
  },
  disabledStyle: {
    opacity: 0.4,
    pointerEvents: 'none',
  },
  variants: {
    active: {
      true: {
        backgroundColor: '$accent',
        borderColor: '$accent',
        disabledStyle: {
          backgroundColor: '$accent',
          borderColor: '$accent',
        },
        hoverStyle: {
          backgroundColor: '$accent-hover',
          borderColor: '$accent-hover',
        },
      },
    },
  },
})

export const SwitchThumb = styled(Switch.Thumb, {
  backgroundColor: '$text-on-accent',
  height: 21,
  width: 21,
  animation: 'quick',
  justifyContent: 'center',
  alignItems: 'center',
  context: HeroSwitchContext,
})

export function HeroSwitch({
  defaultChecked = false,
  icon,
  label,
  disabled = false,
  side = 'right',
}: IHeroSwitchProps) {
  const [isActive, setIsActive] = useState(defaultChecked)
  const switchDirection = side === 'left' ? 'row' : 'row-reverse'
  return (
    <XStack
      alignItems="center"
      gap="$spacing-inset-xxs"
      flexDirection={switchDirection}
    >
      {label && (
        <Label color="$text-primary" htmlFor={label} unstyled>
          {label}
        </Label>
      )}
      <AppHeroSwitch
        checked={isActive}
        disabled={disabled}
        active={isActive}
        onPress={() => setIsActive(!isActive)}
        id={`${label}-switch`}
      >
        <SwitchThumb>
          {icon && <HeroIcon icon={icon} size={11} color="$icon-primary" />}
        </SwitchThumb>
      </AppHeroSwitch>
    </XStack>
  )
}
